import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons for webpack builds
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const DEFAULT_CENTER = [22.5726, 88.3639]; // Kolkata

export const MapPinPicker = ({ latitude, longitude, radiusMeters, onChange }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const radiusRef = useRef(radiusMeters);
  radiusRef.current = radiusMeters;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    const center = (!isNaN(lat) && !isNaN(lng)) ? [lat, lng] : DEFAULT_CENTER;

    const map = L.map(containerRef.current).setView(center, 17);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    const marker = L.marker(center, { draggable: true }).addTo(map);
    const circle = L.circle(center, {
      radius: parseFloat(radiusRef.current) || 0,
      color: '#2563eb',
      weight: 2,
      fillColor: '#3b82f6',
      fillOpacity: 0.12
    }).addTo(map);

    const emit = (latlng) => {
      circle.setLatLng(latlng);
      onChangeRef.current(latlng.lat.toFixed(6), latlng.lng.toFixed(6));
    };

    marker.on('dragend', () => emit(marker.getLatLng()));
    map.on('click', (e) => {
      marker.setLatLng(e.latlng);
      emit(e.latlng);
    });

    mapRef.current = map;
    markerRef.current = marker;
    circleRef.current = circle;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
  }, []);

  // Sync pin when lat/lng change externally (typed input or Get Current Location)
  useEffect(() => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng) || !mapRef.current || !markerRef.current) return;
    const current = markerRef.current.getLatLng();
    if (Math.abs(current.lat - lat) > 1e-6 || Math.abs(current.lng - lng) > 1e-6) {
      markerRef.current.setLatLng([lat, lng]);
      if (circleRef.current) circleRef.current.setLatLng([lat, lng]);
      mapRef.current.setView([lat, lng], mapRef.current.getZoom());
    }
  }, [latitude, longitude]);

  // Update circle when radius changes
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setRadius(parseFloat(radiusMeters) || 0);
    }
  }, [radiusMeters]);

  return (
    <div className="space-y-1" data-testid="map-pin-picker">
      <div ref={containerRef} className="w-full rounded-lg border border-gray-200 z-0" style={{ height: '320px' }} />
      <p className="text-xs text-gray-500">
        Click anywhere on the map or drag the pin to set the exact campus point. The blue circle shows the allowed check-in area{radiusMeters ? ` (${radiusMeters}m)` : ''}.
      </p>
    </div>
  );
};

export default MapPinPicker;
